import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowUpRight, Apple, Sparkles, Heart } from "lucide-react";

const WORK = [
  {
    title: "Midnight Drop",
    brand: "Streetwear Reel",
    tags: ["Reel", "iPhone"],
    video:
      "https://customer-assets.emergentagent.com/job_blinkshots-preview/artifacts/rrgprq2o_14592a90bece496d8e08cdbc0bb1c0af%202.MP4",
    span: "md:col-span-2 md:row-span-2",
    shotOniPhone: true,
  },
  {
    title: "Lens Culture",
    brand: "Creator Series",
    tags: ["UGC", "iPhone"],
    video:
      "https://customer-assets.emergentagent.com/job_blinkshots-preview/artifacts/msfs8iav_39928acd09134e8aaca129a9c6604f0a%202.MP4",
    span: "",
    shotOniPhone: true,
  },
  {
    title: "After Hours Cut",
    brand: "Happy Client Reaction",
    tags: ["Client", "Reel"],
    video:
      "https://customer-assets.emergentagent.com/job_blinkshots-preview/artifacts/e86x8wjz_9434970fbd8b4d0f8ba6b2db8937c92e%202.MP4",
    span: "",
    isClient: true,
  },
  {
    title: "Timeline 04",
    brand: "Vertical Edit",
    tags: ["Edit", "Reel"],
    video:
      "https://customer-assets.emergentagent.com/job_blinkshots-preview/artifacts/o6q36ssj_bb25008768574cc8ad771e27eaa83e94%202.MP4",
    span: "",
    shotOniPhone: true,
  },
  {
    title: "Studio Noir",
    brand: "Fashion Brand Shoot",
    tags: ["Brand", "Reel"],
    video:
      "https://customer-assets.emergentagent.com/job_blinkshots-preview/artifacts/7xlbqa9y_d72bac76b6324567afa66eabf3e4c7a5%202.MP4",
    span: "md:col-span-2",
    shotOniPhone: true,
  },
  {
    title: "Aftermath",
    brand: "Motion System",
    tags: ["Motion", "Promo"],
    img: "https://images.pexels.com/photos/3307758/pexels-photo-3307758.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "",
  },
];

export const Portfolio = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotateX = useTransform(scrollYProgress, [0, 0.4], [8, 0]);

  return (
    <section
      id="work"
      ref={ref}
      data-testid="portfolio-section"
      className="relative py-28 md:py-40 perspective-scene bg-[#050505]"
    >
      <div
        aria-hidden
        className="absolute top-10 -right-40 h-[420px] w-[420px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #4ea8ff, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-20 -left-40 h-[420px] w-[420px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #ff3d8e, transparent 70%)" }}
      />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="neon-streak top-[5%] left-[-10%] right-[-10%]" />

      <div className="relative mx-auto max-w-7xl px-5">
        <motion.div
          style={{ rotateX }}
          className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <p className="mono-eyebrow mb-4" data-testid="portfolio-eyebrow">
              Our Work
            </p>
            <h2
              data-testid="portfolio-heading"
              className="font-display text-4xl md:text-6xl text-white leading-[1.02]"
            >
              Our <span className="text-[#E10600]">Work</span>
            </h2>
          </div>
          <p className="max-w-sm text-neutral-400 font-body">
            Motion pictures shot on the latest iPhone — engineered to stop the scroll and spark
            real reactions.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[260px] md:auto-rows-[280px] gap-4 md:gap-5">
          {WORK.map((w, i) => (
            <WorkCard key={w.title} work={w} index={i} />
          ))}
        </div>

        {/* marquee credit strip */}
        <div className="relative mt-16 overflow-hidden border-y border-white/5 py-6">
          <div className="flex w-max animate-marquee gap-16">
            {[...Array(2)].map((_, loop) => (
              <div key={loop} className="flex items-center gap-16">
                {[
                  "SHORT-FORM",
                  "REELS",
                  "UGC",
                  "SHOT ON IPHONE",
                  "PROMO",
                  "BLINK DELIVERY",
                  "CAMPAIGN",
                  "BRAND",
                  "MOTION",
                  "EDITORIAL",
                ].map((t) => (
                  <span
                    key={`${loop}-${t}`}
                    className="font-display text-3xl md:text-5xl text-white/80"
                  >
                    {t} <span className="text-[#E10600]">·</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WorkCard = ({ work, index }) => {
  const videoRef = useRef(null);

  const handleEnter = () => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  };
  const handleLeave = () => {
    const v = videoRef.current;
    if (v) v.pause();
  };

  return (
    <motion.a
      href="#contact"
      initial={{ opacity: 0, y: 40, rotateX: 8 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.9, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6 }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onTouchStart={handleEnter}
      className={`group relative overflow-hidden rounded-3xl border border-white/5 bg-[#0A0A0A] ${work.span}`}
      data-testid={`work-card-${index}`}
    >
      {work.video ? (
        <video
          ref={videoRef}
          src={work.video}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
          data-testid={`work-video-${index}`}
        />
      ) : (
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 ease-out group-hover:scale-110"
          style={{ backgroundImage: `url('${work.img}')` }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent pointer-events-none" />
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(ellipse_at_center,rgba(225,6,0,0.2),transparent_60%)] pointer-events-none" />

      <div className="absolute top-4 left-4 flex gap-2 flex-wrap z-10">
        {work.tags.map((t) => (
          <span
            key={t}
            className="rounded-full bg-black/60 backdrop-blur px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-white/90 border border-white/10"
          >
            {t}
          </span>
        ))}
      </div>

      {work.shotOniPhone && (
        <div className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur px-3 py-1 border border-white/10 z-10">
          <Apple className="h-3 w-3 text-white fill-white" />
          <span className="text-[10px] font-mono uppercase tracking-wider text-white/90">
            Shot on iPhone
          </span>
        </div>
      )}

      {work.isClient && (
        <div className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full bg-[#E10600]/90 backdrop-blur px-3 py-1 z-10">
          <Heart className="h-3 w-3 text-white fill-white" />
          <span className="text-[10px] font-mono uppercase tracking-wider text-white">
            Happy Client
          </span>
        </div>
      )}

      <div className="absolute top-14 right-4 h-9 w-9 rounded-full glass flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10">
        <Play className="h-3.5 w-3.5 text-white" />
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
        <p className="mono-eyebrow text-neutral-200">{work.brand}</p>
        <div className="mt-1 flex items-end justify-between gap-3">
          <h3 className="font-display text-xl md:text-2xl text-white leading-tight">
            {work.title}
          </h3>
          <ArrowUpRight className="h-5 w-5 text-white translate-y-0 group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform duration-500" />
        </div>
        {work.isClient && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono text-[#E10600]">
            <Sparkles className="h-3 w-3" />
            Instant results. Real reaction.
          </div>
        )}
      </div>
    </motion.a>
  );
};

export default Portfolio;
